import { ChevronDown, ChevronRight } from 'lucide-react'
import { Fragment, useState } from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { TimelineSectionHeading } from '../person-timeline-modal'
import type { groupByKindAndRisk } from './group-by-kind-and-risk'

interface KindRiskTableProps {
	title: string
	groups: ReturnType<typeof groupByKindAndRisk>
	defaultCollapsed?: boolean
}

export function KindRiskTable({
	title,
	groups,
	defaultCollapsed = true,
}: KindRiskTableProps) {
	const [collapsed, setCollapsed] = useState(defaultCollapsed)
	const [collapsedKinds, setCollapsedKinds] = useState<Set<string>>(
		() => new Set(),
	)

	const toggleKind = (kindName: string) => {
		setCollapsedKinds((current) => {
			const next = new Set(current)
			if (next.has(kindName)) {
				next.delete(kindName)
			} else {
				next.add(kindName)
			}
			return next
		})
	}

	return (
		<div>
			<button
				type="button"
				className="flex w-full items-center gap-1"
				onClick={() => setCollapsed((current) => !current)}
			>
				{collapsed ? (
					<ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
				) : (
					<ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
				)}
				<TimelineSectionHeading>{title}</TimelineSectionHeading>
			</button>

			{!collapsed &&
				(groups.length === 0 ? (
					<p className="mt-1 text-xs text-muted-foreground">Sem registros.</p>
				) : (
					<div className="mt-1 rounded-lg border border-kaefer-light-silver">
						<Table className="table-fixed">
							<TableHeader>
								<TableRow className="bg-muted/30">
									<TableHead>Tipo / Risco</TableHead>
									<TableHead className="w-20 text-right">Total</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{groups.map((kind) => {
									const isKindCollapsed = collapsedKinds.has(kind.kindName)
									return (
										<Fragment key={kind.kindName}>
											<TableRow
												className="cursor-pointer bg-muted/20 font-semibold text-kaefer-dark-grey hover:bg-muted/30"
												onClick={() => toggleKind(kind.kindName)}
											>
												<TableCell className="whitespace-normal wrap-break-word">
													<span className="flex items-center gap-1">
														{isKindCollapsed ? (
															<ChevronRight className="size-3.5 shrink-0" />
														) : (
															<ChevronDown className="size-3.5 shrink-0" />
														)}
														{kind.kindName}
													</span>
												</TableCell>
												<TableCell className="text-right">
													{kind.total}
												</TableCell>
											</TableRow>
											{!isKindCollapsed &&
												kind.risks.map((risk) => (
													<TableRow key={`${kind.kindName}-${risk.riskName}`}>
														<TableCell className="whitespace-normal wrap-break-word pl-6 text-muted-foreground">
															{risk.riskName}
														</TableCell>
														<TableCell className="text-right text-muted-foreground">
															{risk.count}
														</TableCell>
													</TableRow>
												))}
										</Fragment>
									)
								})}
							</TableBody>
						</Table>
					</div>
				))}
		</div>
	)
}
